import { ERROR_CODE } from '@/constants';
import {
    accountToAgeGenderAnalytics,
    registrationFlowToAnalyticsSource,
} from '@/libs/analytics/transformers';
import { hasErrorField } from '@/utils/error';
import { EventProperties } from '@calm/evexia-analytics';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR, DS_HEIGHT, PRIMITIVES } from '@calm/evexia-design-tokens';
import { CircleConsentTypes } from '@calm/evexia-web-sdk';
import { BackButtonView } from '@rhg/components/back-button';
import { ConsentDisplay } from '@rhg/components/consent-display';
import { InvertedButton } from '@rhg/components/inverted-button';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { usePostSubmitStep } from '@rhg/hooks/registration/use-next-step';
import {
    getConsentSubmission,
    getNextConsentType,
    getNextConsentTypeInFlow,
} from '@rhg/hooks/use-consent-context';
import {
    ConsentFlow,
    ConsentTypes,
} from '@rhg/hooks/use-consent-context/types';
import { useCurrentLanguage } from '@rhg/hooks/use-current-language';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import { useActivationTokenForPartner } from '@rhg/hooks/use-partner-activation';
import {
    // Search these to see why they are commented out
    // getConsentRegistrationStep,
    // getConsentRegistrationStepMiraculouslyFails,
    useProvidedRegistrationFlowContext,
} from '@rhg/hooks/use-registration-context';
import {
    // ConsentTypeRegistrationStepMap,
    getConsentRegistrationStepMiraculouslyWorks,
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useSponsorCode } from '@rhg/hooks/use-sponsor-code';
import { useStoredSSOTokenPartnerEligibility } from '@rhg/hooks/use-sso-flow';
import { useUpdateConsent, VERSION_ERROR } from '@rhg/hooks/use-update-consent';
import commonMessages from '@rhg/lang/messages/common';
import { logger } from '@rhg/libs/logger';
import { Button, Theme } from '@rhg/libs/material';
import NextImage from '@rhg/libs/next/image';
import { useHipaaSignature } from '@rhg/providers/registration/hipaa-signature';
import {
    buildSponsorCodeEligibilityFormValues,
    EligibilityErrorType,
    hasSponsorCodeOnlyValidations,
} from '@rhg/utils/eligibility';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { RegistrationFlowContainer } from '../container';
import { ConsentRequiredDialog } from './consent-required-dialog';
import consentStepMessages from './messages';

const styleCancelButton = {
    color: (theme: Theme) =>
        theme.palette.mode === 'light' // This conditional is backwards because it is applied on an inverted theme
            ? DS_COLOR.DARK_PRIMARY_MAIN
            : DS_COLOR.LIGHT_PRIMARY_MAIN,
};

const styleGrow = { flexGrow: 1 };

type ConsentMessage = 'accept' | 'heading' | 'title' | 'srTitle';
const consentTypeMessages: Record<
    ConsentMessage,
    Partial<Record<CircleConsentTypes, typeof consentStepMessages.accept>> &
        Record<'default', typeof consentStepMessages.accept>
> = {
    accept: {
        gina: consentStepMessages.accept,
        parental: consentStepMessages.accept,
        default: consentStepMessages.consent,
        in_international: consentStepMessages.accept,
        cn_international: consentStepMessages.accept,
        eu_international: consentStepMessages.accept,
        au_international: consentStepMessages.accept,
        international: consentStepMessages.accept,
    },
    heading: {
        gina: consentStepMessages.authorizationHeading,
        parental: consentStepMessages.parentalConsentHeading,
        default: consentStepMessages.consentHeading,
        in_international: consentStepMessages.consentHeading,
        cn_international: consentStepMessages.consentHeading,
        eu_international: consentStepMessages.consentHeading,
        au_international: consentStepMessages.consentHeading,
        international: consentStepMessages.consentHeading,
    },
    title: {
        gina: consentStepMessages.consentTitle,
        parental: consentStepMessages.parentalConsentHeading,
        default: consentStepMessages.sponsoredConsentTitle,
        in_international: consentStepMessages.sponsoredConsentTitle,
        cn_international: consentStepMessages.sponsoredConsentTitle,
        eu_international: consentStepMessages.sponsoredConsentTitle,
        au_international: consentStepMessages.sponsoredConsentTitle,
        international: consentStepMessages.sponsoredConsentTitle,
    },
    srTitle: {
        gina: consentStepMessages.ginaSrTitle,
        parental: consentStepMessages.parentalConsentHeading,
        default: consentStepMessages.chdppSrTitle,
        in_international: consentStepMessages.chdppSrTitle,
        cn_international: consentStepMessages.chdppSrTitle,
        eu_international: consentStepMessages.chdppSrTitle,
        au_international: consentStepMessages.chdppSrTitle,
        international: consentStepMessages.chdppSrTitle,
    },
};

export const ParentalConsentImage = () => (
    <NextImage
        width={0}
        height={DS_HEIGHT.AVATAR_MEDIUM}
        sizes="100vw"
        style={{
            width: 'auto',
            borderRadius: `${PRIMITIVES.radius._12}px`,
        }}
        data-testid="LOGO"
        alt=""
        src={'/images/age-and-gender.jpg'}
    />
);

/** Use this function to get the correct message for the consent type */
function getConsentTypeMessage(
    message: ConsentMessage,
    consentType: CircleConsentTypes
) {
    return (
        consentTypeMessages[message][consentType] ??
        consentTypeMessages[message].default
    );
}

function getCancelVariant(consentType: CircleConsentTypes) {
    if (consentType === 'gina') {
        return 'outlined';
    }

    return 'contained';
}

function Actions({
    consentType,
    showCancel,
    onCancel,
    trackProps,
}: {
    consentType: CircleConsentTypes;
    showCancel: boolean;
    onCancel: () => void;
    trackProps: EventProperties;
}) {
    const { formatMessage } = useIntl();
    const {
        changeStep,
        submitFlow,
        submitIsPending,
        setHasAcceptedConsent,
        hasAcceptedConsent,
        registrationFlow,
        isSSO,
        consentRequirements,
        newSponsor,
        accountConsents,
        isPostEligibility,
        isFetched: isRegistrationFlowFetched,
        setForcedError,
    } = useProvidedRegistrationFlowContext();
    const { hasSponsorCode, sponsorCode } = useSponsorCode();
    const { activationToken, hasActivationToken } =
        useActivationTokenForPartner(newSponsor?.id);
    const { getNextStep, isFetched: arePostSubmitStepPrerequisitesFetched } =
        usePostSubmitStep();
    const { isEligible: isSSOEligible, isFetched: isSSOFetched } =
        useStoredSSOTokenPartnerEligibility();
    const [hipaaSignature] = useHipaaSignature();
    const { updateConsentAsync } = useUpdateConsent();
    const { refetch: refetchMostRecentSponsorship } =
        useMostRecentSponsorship();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();

    const isFetched =
        (!isSSO || isSSOFetched) &&
        isRegistrationFlowFetched &&
        arePostSubmitStepPrerequisitesFetched;

    const onAgree = async () => {
        try {
            setHasAcceptedConsent(consentType, true);

            const consentTypes = [
                ...ConsentFlow,
                ConsentTypes.HealthcarePortability,
            ].reduce<ConsentTypes[]>((addedTypes, flowType) => {
                if (
                    // Include healthcare portability for change and loss of sponsorship flows
                    (flowType !== ConsentTypes.HealthcarePortability ||
                        [
                            RegistrationFlow.ChangeOfSponsorship,
                            RegistrationFlow.ChangeOfInviteeSponsorship,
                            RegistrationFlow.LossOfSponsorship,
                            RegistrationFlow.UpgradeSponsorship,
                        ].includes(registrationFlow)) &&
                    // Exclude GINA from skip and loss flows
                    (flowType !== ConsentTypes.Gina ||
                        ![
                            RegistrationFlow.SkipSponsorship,
                            RegistrationFlow.LossOfSponsorship,
                        ].includes(registrationFlow))
                ) {
                    addedTypes.push(flowType);
                }
                return addedTypes;
            }, []);

            const minConsentVersions = getConsentSubmission({
                consentRequirements,
                consentTypes,
            });

            // Next consent step after this one
            const nextConsentStep = getConsentRegistrationStepMiraculouslyWorks(
                getNextConsentType({
                    consentRequirements,
                    consentAcceptance: {
                        ...hasAcceptedConsent,
                        [consentType]: true,
                    },
                    consentFlow: getNextConsentTypeInFlow(consentType),
                })
            );

            /**
             * Incredibly these produce different results See https://calmdotcom.atlassian.net/browse/EVE-8642
             * Use with the "sends the user to partner authorization"" test in chdpp.test.tsx
             */
            // console.log('check out this crazy bug', {
            //     ginaConsentType: ConsentTypes.Gina,
            //     ConsentTypeRegistrationStepMap,
            //     stepForGinaConsentTypeViaMap:
            //         ConsentTypeRegistrationStepMap[ConsentTypes.Gina],
            //     bool:
            //         ConsentTypes.Gina &&
            //         ConsentTypeRegistrationStepMap[ConsentTypes.Gina],
            //     works: getConsentRegistrationStepMiraculouslyWorks(
            //         ConsentTypes.Gina
            //     ),
            //     fails: getConsentRegistrationStepMiraculouslyFails(
            //         ConsentTypes.Gina
            //     ),
            //     choosesTheLefthandPath: getConsentRegistrationStep(
            //         ConsentTypes.Gina
            //     ),
            //     nextConsentStep,
            // });

            if (isPostEligibility) {
                await updateConsentAsync({
                    consentContext: consentRequirements[consentType],
                    accountConsentId: accountConsents[0].consentId,
                })
                    .then(async () => {
                        // Refetch synchoronously before moving on to next page
                        await refetchConsent();
                        await refetchMostRecentSponsorship();
                        changeStep(
                            getNextStep({
                                registrationFlow,
                                consentRequirements,
                                consentAcceptance: {
                                    ...hasAcceptedConsent,
                                    [consentType]: true,
                                },
                                consentFlow:
                                    getNextConsentTypeInFlow(consentType),
                            })
                        );
                    })
                    .catch((error: unknown) => {
                        if (
                            hasErrorField(error, 'message') &&
                            error.message === VERSION_ERROR
                        ) {
                            logger.error(
                                'Error from updating consent - required version is 0',
                                {
                                    consentType,
                                    registrationFlow,
                                    acceptedVersion:
                                        consentRequirements[consentType]
                                            .acceptedVersion,
                                    requiredVersion:
                                        consentRequirements[consentType]
                                            .requiredVersion,
                                    isRequired:
                                        consentRequirements[consentType]
                                            .isRequired,
                                    hasRequirement:
                                        consentRequirements[consentType]
                                            .hasRequirement,
                                    isReconsent:
                                        consentRequirements[consentType]
                                            .isReconsent,
                                    link: consentRequirements[consentType].link,
                                    scope: consentRequirements[consentType]
                                        .scope,
                                },
                                error as Error
                            );
                            setForcedError('generic');
                        }
                    });
            } else if (registrationFlow === RegistrationFlow.SkipSponsorship) {
                // Should only be reachable by CHDPP and parental
                const submission = await submitFlow({
                    registrationFlow,
                    minConsentVersions,
                });
                // Skip is a special case where we want to manually remove the requirements except CHDPP and parental
                changeStep(
                    getNextStep({
                        ...submission,
                        consentRequirements,
                        consentAcceptance: {
                            ...hasAcceptedConsent,
                            [ConsentTypes.Chdpp]: true,
                            ...(consentType === 'parental'
                                ? { [ConsentTypes.Parental]: true }
                                : {}),
                        },
                        consentFlow: [ConsentTypes.Chdpp],
                    })
                );
            } else if (
                registrationFlow === RegistrationFlow.LossOfSponsorship
            ) {
                // Should only be reachable by CHDPP and parental
                const submission = await submitFlow({
                    registrationFlow,
                    hipaaSignature,
                    minConsentVersions,
                });
                const nextStep = getNextStep({
                    ...submission,
                    consentAcceptance: {
                        ...hasAcceptedConsent,
                        [consentType]: true,
                    },
                });
                changeStep(nextStep);
            } else if (nextConsentStep) {
                changeStep(nextConsentStep);
            } else if (
                registrationFlow === RegistrationFlow.NewInviteeRegistration ||
                registrationFlow ===
                    RegistrationFlow.ExistingInviteeRegistration ||
                registrationFlow === RegistrationFlow.ChangeOfInviteeSponsorship
            ) {
                const submission = await submitFlow({
                    registrationFlow,
                    minConsentVersions,
                });
                changeStep(
                    getNextStep({
                        ...submission,
                        consentAcceptance: {
                            ...hasAcceptedConsent,
                            [consentType]: true,
                        },
                    })
                );
            } else if (isSSO && isSSOEligible) {
                const submission = await submitFlow({
                    registrationFlow,
                    isSSO,
                    willUseSSOTokenEligibility: true,
                    minConsentVersions,
                });
                changeStep(
                    getNextStep({
                        ...submission,
                        consentAcceptance: {
                            ...hasAcceptedConsent,
                            [consentType]: true,
                        },
                    })
                );
            } else if (hasActivationToken) {
                try {
                    const submission = await submitFlow({
                        isSSO: false,
                        willUseSSOTokenEligibility: false,
                        registrationFlow,
                        eligibilityFormValues: [],
                        activationToken,
                        minConsentVersions,
                    });
                    changeStep(
                        getNextStep({
                            ...submission,
                            consentAcceptance: {
                                ...hasAcceptedConsent,
                                [consentType]: true,
                            },
                        })
                    );
                } catch (error) {
                    logger.error(
                        ERROR_CODE.INVALID_ACTIVATION_TOKEN,
                        { partnerId: newSponsor?.id },
                        error as Error
                    );
                    // A token-driven submission has no manual eligibility
                    // form to fall back to; let submitFlowError surface via
                    // the container.
                }
            } else if (hasSponsorCode) {
                if (!hasSponsorCodeOnlyValidations(newSponsor)) {
                    // sponsor_code field doesn't exist in partner config — fall back to eligibility
                    logger.error(
                        ERROR_CODE.ELIGIBILITY_FIELDS_VALIDATION_ERROR,
                        {
                            accessCode: newSponsor?.uniqueCode,
                            sponsorCode,
                        },
                        new Error(
                            ERROR_CODE.ELIGIBILITY_FIELDS_VALIDATION_ERROR
                        )
                    );
                    setForcedError(EligibilityErrorType.EligibilityDenied);
                    changeStep(RegistrationFlowStep.Eligibility);
                    return;
                }

                try {
                    const submission = await submitFlow({
                        isSSO: false,
                        willUseSSOTokenEligibility: false,
                        registrationFlow,
                        eligibilityFormValues:
                            buildSponsorCodeEligibilityFormValues(sponsorCode),
                        minConsentVersions,
                    });
                    changeStep(
                        getNextStep({
                            ...submission,
                            consentAcceptance: {
                                ...hasAcceptedConsent,
                                [consentType]: true,
                            },
                        })
                    );
                } catch (error) {
                    logger.error(
                        ERROR_CODE.INVALID_SPONSOR_CODE,
                        { accessCode: newSponsor?.uniqueCode, sponsorCode },
                        error as Error
                    );
                    // Fall back to eligibility screen
                    changeStep(RegistrationFlowStep.Eligibility);
                }
            } else {
                changeStep(RegistrationFlowStep.Eligibility);
            }
        } catch (error) {
            logger.error(
                `Error from ${consentType} step`,
                {
                    hasRequirement:
                        consentRequirements[consentType].hasRequirement,
                    isRequired: consentRequirements[consentType].hasRequirement,
                    isSSO,
                    registrationFlow,
                },
                error as Error
            );
        }
    };

    return (
        <>
            {showCancel && (
                <Button
                    trackButton="Cancel"
                    trackProps={trackProps}
                    variant={getCancelVariant(consentType)}
                    color="primary"
                    size="large"
                    onClick={onCancel}
                    sx={styleGrow}
                    disabled={!isFetched || submitIsPending}
                >
                    {formatMessage(commonMessages['common.cancel'])}
                </Button>
            )}
            <Button
                trackButton="Accept"
                trackProps={trackProps}
                variant="contained"
                color="primary"
                size="large"
                onClick={onAgree}
                disabled={!isFetched || submitIsPending}
                sx={styleGrow}
            >
                {formatMessage(getConsentTypeMessage('accept', consentType))}
            </Button>
        </>
    );
}

// The combination of our full screen content with an iframe causes a strange
// scrolling bug. This is a workaround for it.
const styleFixOverflow = {
    '& > div:first-of-type': {
        overflowY: 'initial',
    },
};

interface ConsentStepProps {
    consentType: CircleConsentTypes;
}
export const ConsentStep = ({ consentType }: ConsentStepProps) => {
    const currentLanguage = useCurrentLanguage();
    const {
        isSSO,
        isFetched,
        submitIsPending,
        changeStep,
        exitFlow,
        newSponsor,
        currentSponsorPartner,
        registrationFlow,
        accessCodeState,
        inviteCodeState,
        consentRequirements,
        isPostEligibility,
        account,
    } = useProvidedRegistrationFlowContext();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { push } = useRouter();

    const { formatMessage } = useIntl();

    if (!isFetched && !submitIsPending) {
        return <FullWidthLinearProgress />;
    }

    const showBack =
        consentType === ConsentTypes.Parental || (!isSSO && !isPostEligibility);

    const showCancelTopRight = Boolean(
        !isPostEligibility &&
            ![
                RegistrationFlow.NewRegistration,
                RegistrationFlow.NewInviteeRegistration,
                RegistrationFlow.SkipSponsorship,
            ].includes(registrationFlow)
    );

    const skipCancel =
        consentType !== CircleConsentTypes.Gina &&
        consentType !== CircleConsentTypes.Parental;

    /** Show on GINA if we have an exitFlow() and are not SSO. Always show on parental */
    const showCancel = Boolean(
        consentType !== ConsentTypes.Parental &&
            (skipCancel || (exitFlow && !isSSO))
    );

    const onBack = () => {
        if (consentType === ConsentTypes.Parental) {
            return changeStep(RegistrationFlowStep.AgeGender);
        }

        if (
            [
                RegistrationFlow.ChangeOfSponsorship,
                RegistrationFlow.ChangeOfInviteeSponsorship,
            ].includes(registrationFlow)
        ) {
            return changeStep(RegistrationFlowStep.ChangeSponsorship);
        }

        if (
            registrationFlow === RegistrationFlow.NewRegistration &&
            accessCodeState?.isAccessCodeFromQuery
        ) {
            return changeStep(RegistrationFlowStep.Eligibility);
        }

        if (registrationFlow === RegistrationFlow.LossOfSponsorship) {
            if (!accessCodeState?.accessCode) {
                return changeStep(RegistrationFlowStep.Hipaa);
            }
        }

        if (
            [
                RegistrationFlow.NewInviteeRegistration,
                RegistrationFlow.SkipSponsorship,
            ].includes(registrationFlow)
        ) {
            return changeStep(RegistrationFlowStep.SelectCountry);
        }

        if (registrationFlow === RegistrationFlow.UpgradeSponsorship) {
            // An earlier owed consent (e.g. Gina before Chdpp) still has
            // somewhere real to go back to. Only when there isn't one does
            // Upgrade have nothing to return to — it never shows a real
            // access code form (seeded by TransitionDialogManager.onUpgrade,
            // not typed in) — so cancel the flow instead.
            const previousConsentStep =
                getConsentRegistrationStepMiraculouslyWorks(
                    getNextConsentType({
                        consentRequirements,
                        consentFlow: ConsentFlow.slice(
                            0,
                            ConsentFlow.indexOf(consentType)
                        ),
                        reverse: true,
                    })
                );
            if (previousConsentStep) {
                return changeStep(previousConsentStep);
            }
            if (exitFlow) {
                exitFlow().catch((err: Error) =>
                    logger.error('Error calling exitFlow', {}, err)
                );
            }
            return;
        }

        return changeStep(RegistrationFlowStep.AccessCode);
    };

    const onCancel = () => {
        if (exitFlow) {
            exitFlow().catch((err: Error) =>
                logger.error('Error calling exitFlow', {}, err)
            );
        } else if (
            // Reachable via loss of sponsorship screen (when we regain via access code)
            registrationFlow === RegistrationFlow.ChangeOfSponsorship &&
            accessCodeState?.isAccessCodeFromQuery
        ) {
            push(routes.dashboard).catch((err: Error) =>
                logger.error(
                    'Error redirecting to dashboard from consent cancel',
                    {},
                    err
                )
            );
        } else if (
            // Reachable via loss of sponsorship screen (when we regain via invite)
            registrationFlow === RegistrationFlow.ChangeOfInviteeSponsorship &&
            inviteCodeState?.isInviteCodeFromQuery
        ) {
            push(routes.dashboard).catch((err: Error) =>
                logger.error(
                    'Error redirecting to dashboard from consent cancel',
                    {},
                    err
                )
            );
        } else {
            setIsDialogOpen(true);
        }
    };

    const showCalmHealthDefault =
        registrationFlow === RegistrationFlow.SkipSponsorship;

    const parentalConsentProps: EventProperties = {
        Source: registrationFlowToAnalyticsSource(registrationFlow),
        ...(account ? accountToAgeGenderAnalytics(account) : {}),
    };

    const trackProps: EventProperties = {
        ConsentType: consentType,
        ConsentVersion: `v${consentRequirements[consentType].requiredVersion}`,
        IsDependent: [
            RegistrationFlow.ChangeOfInviteeSponsorship,
            RegistrationFlow.NewInviteeRegistration,
            RegistrationFlow.ExistingInviteeRegistration,
        ].includes(registrationFlow),
        PartnerID: isPostEligibility
            ? currentSponsorPartner?.id
            : newSponsor?.id,
        ...(consentType === ConsentTypes.Parental ? parentalConsentProps : {}),
    };

    return (
        <RegistrationFlowContainer
            screenName="Consent"
            featureName="Consent"
            trackProps={trackProps}
            leftAction={
                showBack && (
                    <BackButtonView
                        trackButton="Back"
                        ButtonProps={{
                            trackProps,
                        }}
                        onClick={onBack}
                    />
                )
            }
            fullHeightContent
            rightAction={
                showCancelTopRight ? (
                    <InvertedButton
                        trackButton="Cancel"
                        trackProps={trackProps}
                        color="primary"
                        sx={styleCancelButton}
                        onClick={onCancel}
                        size="large"
                    >
                        {formatMessage(commonMessages['common.cancel'])}
                    </InvertedButton>
                ) : null
            }
            bottomActions={
                <Actions
                    consentType={consentType}
                    showCancel={showCancel}
                    onCancel={onCancel}
                    trackProps={trackProps}
                />
            }
            title={formatMessage(getConsentTypeMessage('heading', consentType))}
            sx={styleFixOverflow}
        >
            <ConsentDisplay
                sponsorPartner={newSponsor}
                consentContext={consentRequirements[consentType]}
                title={formatMessage(
                    getConsentTypeMessage('title', consentType),
                    {
                        name: showCalmHealthDefault
                            ? formatMessage(commonMessages['common.brand_name'])
                            : newSponsor?.displayName,
                    }
                )}
                imageOverride={
                    consentType === CircleConsentTypes.Parental && (
                        <ParentalConsentImage />
                    )
                }
                showCalmHealthDefault={showCalmHealthDefault}
                srTitle={formatMessage(
                    getConsentTypeMessage('srTitle', consentType)
                )}
                fallbackUrl={
                    routes.external.consentFallback?.[consentType]
                        ? routes.external.consentFallback[consentType](
                              currentLanguage
                          )
                        : undefined
                }
            />
            {isDialogOpen && (
                <ConsentRequiredDialog setIsDialogOpen={setIsDialogOpen} />
            )}
        </RegistrationFlowContainer>
    );
};
